import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  CardTabs,
  DestructiveModal,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanelList,
  Text,
  TextButton,
  Toast,
  Tooltip,
  useConfirmModal,
} from '@modusign/design-system';
import { useChangeRoles } from '@modusign/openapi-client-category';
import { createContext, RefObject, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';

import CenterLoadingSpinner from '@app/components/CenterLoadingSpinner';
import {
  ResourceModalBody,
  ResourceModalFooter,
  ResourceModalHeaderWithBackButton,
} from '@app/components/Modal/ResourceModal';
import { ResourceModalFooterProps } from '@app/components/Modal/ResourceModal/ResourceModalFooter';
import RootLoadingSpinner from '@app/containers/Loading/RootLoadingSpinner';
import { ResourceRoleSubject } from '@app/features/authorization';
import AddAssigneeForm from '@app/features/authorization/components/PermissionAssigneeListModal/AddAssigneeForm';
import LabelBasicInfoTabPanel from '@app/features/label/components/LabelInfoModal/LabelBasicInfoTabPanel';
import LabelViewerTabPanel from '@app/features/label/components/LabelViewerTabPanel';
import useResourceLabel from '@app/features/label/hooks/useResourceLabel';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import {
  useCurrentMemberGroupList,
  useCurrentUserMemberId,
  useCurrentUserRole,
} from '@app/hooks/workspace';
import pushAdobeDataLayer from '@app/libs/analytics/adobe';
import { isAxiosError } from '@app/libs/axios/isAxiosError';
import { logger } from '@app/libs/logger';
import { useInvalidateDocumentList } from '@app/routes/Documents/useDocumentList';
import { getName } from '@app/store/user/user/selectors';

import useLabelRoleList from '../../hooks/useLabelRoleList';
import { ChangeToPrivateLabelButton } from '../ChangeToPrivateLabelButton';
import { ChangeToPublicLabelButton } from '../ChangeToPublicLabelButton';

export default function LabelInfoModalContent({
  id,
  onClose,
  onBack,
}: {
  id: string;
  onClose: () => void;
  onBack?: () => void;
}) {
  const [tab, setTab] = useState<LabelInfoTab>('INFO');
  const footerRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState<'MAIN' | 'ADD_VIEWER'>('MAIN');

  const { data: label, error } = useResourceLabel(id);

  useEffect(() => {
    // 라벨 접근 권한이 사라지면 모달을 닫습니다.
    if (isAxiosError(error) && error.response?.status === 403) {
      // TODO(@atobaum): Toast 띄우기
      (onBack || onClose)();
    }
  }, [error, onBack, onClose]);

  if (page === 'ADD_VIEWER') {
    return (
      <>
        <ResourceModalHeaderWithBackButton
          resourceName={label?.name}
          title="라벨"
          onBack={() => setPage('MAIN')}
          onClose={onClose}
        />
        <ResourceModalBody>
          <LabelInfoModalFooterContext.Provider value={footerRef}>
            <AddViewerContent
              id={id}
              onCancel={() => setPage('MAIN')}
              onComplete={() => setPage('MAIN')}
            />
          </LabelInfoModalFooterContext.Provider>
        </ResourceModalBody>
        <div ref={footerRef} style={{ width: '100%' }} />
      </>
    );
  }

  return (
    <>
      <ResourceModalHeaderWithBackButton
        resourceName={label?.name}
        title="라벨"
        onBack={onBack}
        onClose={onClose}
      />
      <ResourceModalBody>
        <LabelInfoModalFooterContext.Provider value={footerRef}>
          <LabelInfoModalBody
            id={id}
            tab={tab}
            onAddViewerClick={() => setPage('ADD_VIEWER')}
            onBack={onBack || onClose}
            onTabChange={setTab}
          />
        </LabelInfoModalFooterContext.Provider>
      </ResourceModalBody>
      <div ref={footerRef} style={{ width: '100%' }} />
    </>
  );
}

type LabelInfoTab = 'INFO' | 'VIEWER';

interface LabelInfoModalBodyProps {
  id: string;
  tab: LabelInfoTab;
  onTabChange: (tab: LabelInfoTab) => void;
  onAddViewerClick: () => void;
  onBack: () => void;
}

function LabelInfoModalBody({
  id,
  onAddViewerClick,
  onBack,
  tab,
  onTabChange,
}: LabelInfoModalBodyProps) {
  const workspaceId = useWorkspaceId();
  const memberId = useCurrentUserMemberId();
  const currentUserRole = useCurrentUserRole();
  const currentUserGroups = useCurrentMemberGroupList();
  const { createErrorHandler } = useOpenErrorModal();
  const confirmModal = useConfirmModal();
  const userName = useSelector(getName);

  const isMobileWidth = useResponsiveValue([true, false, false]);

  const {
    data: label,
    isLoading: isLabelLoading,
    error: labelError,
    refetch: refetchLabel,
  } = useResourceLabel(id);
  const {
    data: roles,
    isLoading: isRoleListLoading,
    error: roleListError,
    refetch: refetchRoleList,
  } = useLabelRoleList(id);
  const { mutate, isLoading: isChangeRoles } = useChangeRoles({
    mutation: {
      onSuccess: (data, variables) => {
        pushAdobeDataLayer({
          event: 'changeLabelRoles',
          isWorkspaceLabel: variables.data.roles[0]?.subjectType === 'WORKSPACE',
        });
        refetchRoleList();
        // resource role 수정 후 현 멤버의 라벨 접근권한 체크를 위해 라벨을 다시 불러옵니다.
        // 그런데 서버에서 권한 업데이트가 비동기적으로 일어나 시간이 좀 걸리기 때문에
        // 0.5초 이후에 다시 불러오도록 합니다.
        setTimeout(() => {
          refetchLabel();
        }, 500);
      },
      onError: createErrorHandler({ title: '멤버/그룹 삭제 실패' }),
    },
  });

  const invalidateLabels = useInvalidateDocumentList();

  if (isLabelLoading || isRoleListLoading) {
    return (
      <Box height="200px" position="relative" width="100%">
        <CenterLoadingSpinner size={36} />
      </Box>
    );
  }

  if (labelError || roleListError) {
    return (
      <Flex alignItems="center" flexDirection="column" height="200px" justifyContent="center">
        <Text>라벨 정보를 불러오지 못했습니다.</Text>
        <TextButton
          mt={2}
          onClick={() => {
            if (labelError) {
              refetchLabel();
            }
            if (roleListError) {
              refetchRoleList();
            }
          }}
        >
          다시 시도
        </TextButton>
      </Flex>
    );
  }

  if (!label || !roles) {
    logger.error('Inaccessible branch of <LabelInfoModal />');
    return null;
  }

  const isPublicLabel = roles.some((r) => r.subjectType === 'WORKSPACE');

  return (
    <CardTabs
      gap={8}
      index={tab === 'INFO' ? 0 : 1}
      isFitted
      onChange={(index) => onTabChange(index === 0 ? 'INFO' : 'VIEWER')}
    >
      <TabList>
        <Tab>기본 정보</Tab>
        <Tab>
          볼 수 있는 멤버
          <Tooltip
            content={
              <Text as="p">
                라벨 보기·수정·삭제가 가능하며
                <br />
                문서·계약 등에 적용할 수 있습니다.
              </Text>
            }
          >
            <Box color="textNeutralLight" display="inline-block" fontSize="14px" ml="2px" p="3px">
              <FontAwesomeIcon icon={faQuestionCircle} />
            </Box>
          </Tooltip>
        </Tab>
      </TabList>
      <TabPanelList>
        <TabPanel>
          <LabelBasicInfoTabPanel id={id} onBack={onBack} />
        </TabPanel>
        <TabPanel>
          <LabelViewerTabPanel
            roles={roles}
            onAddClick={onAddViewerClick}
            onRemove={async (roles) => {
              // 삭제할 것에 유저가 포함되어 있으면 안내 모달을 띄웁니다.
              if (
                currentUserRole?.type !== 'ADMIN' &&
                roles.some((rr) => {
                  if (rr.subjectType === 'MEMBER') {
                    return rr.subjectId === memberId;
                  }
                  return currentUserGroups.some((g) => g.id === rr.subjectId);
                })
              ) {
                const hasConfirmed = await confirmModal.openModal();
                if (!hasConfirmed) {
                  return;
                }
              }

              mutate(
                {
                  workspaceId,
                  labelId: id,
                  data: {
                    roles: roles.map(({ subjectId, subjectType }) => ({
                      // name이 null이면 제거를 의미합니다.
                      name: null,
                      subjectId,
                      subjectType,
                    })),
                  },
                },
                {
                  onSuccess: () => {
                    invalidateLabels();
                    Toast.success({ description: '선택한 멤버·그룹을 삭제했습니다.' });
                  },
                }
              );
            }}
          />
          {!isMobileWidth &&
            (isPublicLabel ? (
              <ChangeToPrivateLabelButton
                needConfirm
                onConfirm={() => {
                  return mutate({
                    workspaceId,
                    labelId: id,
                    data: {
                      roles: [{ subjectType: 'MEMBER', subjectId: memberId, name: 'EDITOR' }],
                    },
                  });
                }}
              />
            ) : (
              <ChangeToPublicLabelButton
                needConfirm
                onConfirm={() => {
                  return mutate({
                    workspaceId,
                    labelId: id,
                    data: {
                      roles: [{ subjectType: 'WORKSPACE', subjectId: workspaceId, name: 'EDITOR' }],
                    },
                  });
                }}
              />
            ))}
        </TabPanel>
      </TabPanelList>
      <DestructiveModal
        closeButtonLabel="취소"
        confirmButtonLabel="삭제"
        maxWidth="xs"
        {...confirmModal.modalProps}
        title="볼 수 있는 멤버에서 삭제"
      >
        <Text as="p" textAlign="center">
          선택한 멤버·그룹에 {userName}님이 포함되어 있습니다.
          <br />더 이상 라벨을 이용하지 못할 수 있습니다.
        </Text>
      </DestructiveModal>
      {isChangeRoles || <RootLoadingSpinner />}
    </CardTabs>
  );
}
const LabelInfoModalFooterContext = createContext<RefObject<HTMLDivElement> | null>(null);
function useLabelInfoContext() {
  return useContext(LabelInfoModalFooterContext);
}

export function LabelInfoModalFooter(props: ResourceModalFooterProps) {
  const footerRef = useLabelInfoContext();
  if (!footerRef?.current) {
    return null;
  }
  return createPortal(<ResourceModalFooter {...props} />, footerRef.current);
}

function AddViewerContent({
  id,
  onCancel,
  onComplete,
}: {
  id: string;
  onCancel: () => void;
  onComplete: () => void;
}) {
  const { data: roles, refetch } = useLabelRoleList(id);
  const workspaceId = useWorkspaceId();
  const { createErrorHandler } = useOpenErrorModal();
  const [selected, setSelected] = useState<ResourceRoleSubject[]>([]);
  const { mutate, isLoading } = useChangeRoles({
    mutation: {
      onSuccess: () => {
        refetch();
        Toast.success({ description: '선택한 멤버·그룹을 추가했습니다.' });
        onComplete();
      },
      onError: createErrorHandler({ title: '멤버/그룹 추가 실패' }),
    },
  });

  if (!roles) {
    // resource role을 불러오지 않으면 추가 버튼을 누를 수 없어 이 컴포넌트가 렌더되지 않습니다.
    return null;
  }

  return (
    <>
      {isLoading && <RootLoadingSpinner />}
      <AddAssigneeForm subjects={selected} onChange={setSelected} />
      <LabelInfoModalFooter>
        <Flex justifyContent="flex-end" width="100%">
          <Button disabled={isLoading} variant="subtle" weight="light" onClick={onCancel}>
            취소
          </Button>
          <Button
            ml={3}
            type="submit"
            variant="primary"
            weight="light"
            onClick={() => {
              mutate({
                workspaceId,
                labelId: id,
                data: {
                  roles: roles.concat(selected).map((v) => ({
                    name: 'EDITOR',
                    subjectId: v.subjectId,
                    subjectType: v.subjectType,
                  })),
                },
              });
            }}
          >
            추가
          </Button>
        </Flex>
      </LabelInfoModalFooter>
    </>
  );
}
